"use client";
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import axios from "axios";

const UserActivityDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("year");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChartData = async (period) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `/api/v1/get-total-download-sample-users?filterType=${period}`,
        { withCredentials: true }
      );

      const result = response.data;

      if (result.success && Array.isArray(result.data)) {
        setChartData(result.data);
      } else {
        console.error("Unexpected response:", result);
        setError("Invalid data format from API");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to load chart data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData(selectedPeriod);
  }, [selectedPeriod]);

  const axisTickColor = "#475569";
  const lineStrokeColor = "#2563eb";
  const dotFillColor = "#2563eb";
  const dotStrokeColor = "#e0e7ff";

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${value / 1000000}M`;
    if (value >= 1000) return `${value / 1000}k`;
    return value;
  };

  const formatValue = (value) =>
    value >= 1000000
      ? `${(value / 1000000).toFixed(1)}M`
      : value >= 1000
      ? `${(value / 1000).toFixed(1)}k`
      : value.toString();

  const getSummaryStats = () => {
    if (chartData.length === 0)
      return {
        current: "0",
        growth: "0%",
        total: "0",
        currentLabel: "",
        totalLabel: "",
      };

    const nonZeroData = chartData.filter((item) => item.count > 0);
    const current = nonZeroData.at(-1)?.count || 0;
    const previous = nonZeroData.at(-2)?.count || 0;
    const growth =
      previous > 0 ? Math.round(((current - previous) / previous) * 100) : 0;
    const total = chartData.reduce((sum, item) => sum + item.count, 0);

    return {
      current: formatValue(current),
      growth: `${growth > 0 ? "+" : ""}${growth}%`,
      total: formatValue(total),
      currentLabel:
        selectedPeriod === "week"
          ? "Today"
          : selectedPeriod === "month"
          ? "Current Month"
          : "Current Year",
      totalLabel:
        selectedPeriod === "week"
          ? "This Week"
          : selectedPeriod === "month"
          ? "YTD Total"
          : "5-Year Total",
    };
  };

  const summaryStats = getSummaryStats();

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid transparent",
        transition: "all 0.3s ease",
        boxShadow: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 
                    0 4px 6px -4px rgba(0, 0, 0, 0.1)`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 20px -4px rgba(233, 223, 223, 0.15), 
                      0 4px 6px -4px rgba(0, 0, 0, 0.05)`,
          borderColor: "rgba(109, 102, 102, 0.1)",
        },
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      {/* Header and period selector */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6" color="text.secondary" fontWeight="bold">
          Download User Activity
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              bgcolor: "background.default",
              borderRadius: 1,
            }}
          >
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
            <MenuItem value="year">Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 320, mb: 3, minWidth: 0 }}>
        {loading ? (
          <Typography>Loading chart...</Typography>
        ) : error ? (
          <Typography color="error.main">{error}</Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, bottom: 10 }}>
              <XAxis
                dataKey="label"
                tick={{ fill: axisTickColor, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                minTickGap={15}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fill: axisTickColor, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip formatter={(value) => formatValue(value)} />
              <Line
                type="monotone"
                dataKey="count"
                stroke={lineStrokeColor}
                strokeWidth={3}
                dot={{
                  fill: dotFillColor,
                  strokeWidth: 2,
                  r: 4,
                  stroke: dotStrokeColor,
                }}
                activeDot={{
                  r: 6,
                  fill: dotFillColor,
                  stroke: dotStrokeColor,
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* Summary stats */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "center",
          mt: 3,
        }}
      >
        <Box
          sx={{
            flex: "1 1 120px",
            minWidth: 120,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" color="primary.main" noWrap>
            {summaryStats.current}
          </Typography>
          <Typography variant="body2" noWrap>
            {summaryStats.currentLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 120px",
            minWidth: 120,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" color="success.main" noWrap>
            {summaryStats.growth}
          </Typography>
          <Typography variant="body2" noWrap>
            Growth
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 120px",
            minWidth: 120,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" color="text.secondary" noWrap>
            {summaryStats.total}
          </Typography>
          <Typography variant="body2" noWrap>
            {summaryStats.totalLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default UserActivityDashboard;
